import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import FormDialog from "@/components/admin/FormDialog";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import {
  ImageIcon,
  Images,
  ClipboardList,
  Settings as SettingsIcon,
  LogOut,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Loader2,
  Save,
  Upload,
  Pencil,
  Link as LinkIcon,
} from "lucide-react";

const TABS = [
  { id: "carousel", label: "Hero Carousel", icon: ImageIcon },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "menu", label: "Menu", icon: ClipboardList },
  { id: "settings", label: "Site Info", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("carousel");

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F4F5F7] text-[#111827]">
      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 hidden md:flex md:flex-col">
          <Link to="/" className="flex items-center gap-3 px-5 py-5 border-b border-gray-200">
            <img src="/logo-emilios.jpg" alt="Emilio's" className="h-11 w-auto rounded-md" />
          </Link>
          <nav className="p-3 flex-1 space-y-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? "bg-[var(--terracotta)]/10 text-[var(--terracotta)]"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Icon size={16} />
                  {t.label}
                </button>
              );
            })}
          </nav>
          <div className="p-4 border-t border-gray-200">
            <div className="text-xs text-gray-500 mb-2 truncate">{user?.email}</div>
            <button
              onClick={doLogout}
              data-testid="admin-logout-btn"
              className="w-full inline-flex items-center gap-2 justify-center px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500">
                Dashboard
              </div>
              <h1 className="font-serif text-2xl font-semibold">
                {TABS.find((t) => t.id === tab)?.label}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/"
                target="_blank"
                data-testid="admin-view-site"
                className="text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                View site
              </Link>
              <button
                onClick={doLogout}
                data-testid="admin-logout-btn-header"
                className="md:hidden inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm"
              >
                <LogOut size={14} />
              </button>
            </div>
          </header>

          <div className="p-6">
            {tab === "carousel" && <ImagesTab endpoint="hero-images" title="hero carousel" testPrefix="carousel" />}
            {tab === "gallery" && <ImagesTab endpoint="gallery-images" title="gallery" testPrefix="gallery" />}
            {tab === "menu" && <MenuTab />}
            {tab === "settings" && <SettingsTab />}
          </div>
        </main>
      </div>
    </div>
  );
}

/* -------------- Carousel Tab -------------- */
function ImagesTab({ endpoint, title, testPrefix }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [urlDialog, setUrlDialog] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/content/${endpoint}`);
      setImages(r.data);
    } catch (e) {
      toast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
     
  }, [endpoint]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await api.post(`/content/${endpoint}`, {
          data_url: ev.target.result,
          caption: file.name,
          order: images.length,
        });
        toast.success("Image added");
        await load();
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Upload failed");
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/content/${endpoint}/${confirmDel.id}`);
      toast.success("Removed");
      setConfirmDel(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= images.length) return;
    const a = images[idx];
    const b = images[target];
    try {
      await Promise.all([
        api.put(`/content/${endpoint}/${a.id}`, { order: b.order }),
        api.put(`/content/${endpoint}/${b.id}`, { order: a.order }),
      ]);
      load();
    } catch {
      toast.error("Reorder failed");
    }
  };

  const addUrl = async (values) => {
    try {
      await api.post(`/content/${endpoint}`, {
        data_url: values.url.trim(),
        caption: values.caption || "",
        order: images.length,
      });
      toast.success("Image added");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add");
      throw err;
    }
  };

  const isHero = endpoint === "hero-images";
  const helpText = isHero
    ? "Manage the rotating images shown at the top of your homepage."
    : "Manage the photo gallery shown on your homepage.";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <p className="text-sm text-gray-600 max-w-lg">
          {helpText} JPG, PNG, WebP or GIF. Max 5 MB per image.
        </p>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={onFile}
            data-testid={`${testPrefix}-file-input`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testPrefix}-upload-btn`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--terracotta)] text-white text-sm font-medium hover:bg-[var(--terracotta-hover)] disabled:opacity-60"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Upload image
          </button>
          <button
            onClick={() => setUrlDialog(true)}
            data-testid={`${testPrefix}-add-url-btn`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50"
          >
            <LinkIcon size={14} /> Add by URL
          </button>
        </div>
      </div>

      {loading ? (
        <SkeletonGrid />
      ) : images.length === 0 ? (
        <EmptyState label="No images yet. Upload one to get started." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testPrefix}-list`}>
          {images.map((img, idx) => (
            <div
              key={img.id}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              data-testid={`${testPrefix}-item-${idx}`}
            >
              <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
                <img
                  src={img.data_url}
                  alt={img.caption || title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="text-xs text-gray-600 truncate">
                  #{idx + 1} · {img.caption || "Untitled"}
                </div>
                <div className="flex items-center gap-1">
                  <IconBtn testid={`${testPrefix}-up-${idx}`} onClick={() => move(idx, -1)} disabled={idx === 0}>
                    <ArrowUp size={14} />
                  </IconBtn>
                  <IconBtn testid={`${testPrefix}-down-${idx}`} onClick={() => move(idx, 1)} disabled={idx === images.length - 1}>
                    <ArrowDown size={14} />
                  </IconBtn>
                  <IconBtn testid={`${testPrefix}-delete-${idx}`} onClick={() => setConfirmDel(img)} danger>
                    <Trash2 size={14} />
                  </IconBtn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <FormDialog
        open={urlDialog}
        onOpenChange={setUrlDialog}
        title="Add image by URL"
        description="Paste a direct link to a JPG, PNG, WebP or GIF."
        fields={[
          { name: "url", label: "Image URL", placeholder: "https://…", required: true },
          { name: "caption", label: "Caption (optional)", placeholder: "e.g. Margherita Pizza" },
        ]}
        initialValues={{ url: "", caption: "" }}
        submitLabel="Add image"
        onSubmit={addUrl}
        testIdPrefix={`${testPrefix}-url`}
      />

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        title="Remove this image?"
        description={`It will no longer appear in the ${title}.`}
        confirmLabel="Remove"
        onConfirm={remove}
        testIdPrefix={`${testPrefix}-del-confirm`}
      />
    </div>
  );
}

/* -------------- Menu Tab -------------- */
function MenuTab() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(null);

  // Dialog state
  const [catDialog, setCatDialog] = useState(null); // { mode: "add"|"edit", data? }
  const [itemDialog, setItemDialog] = useState(null); // { mode, data? }
  const [confirmCat, setConfirmCat] = useState(null);
  const [confirmItem, setConfirmItem] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get("/content/categories"),
        api.get("/content/menu-items"),
      ]);
      setCategories(c.data);
      setItems(m.data);
      if (!activeCat && c.data.length > 0) setActiveCat(c.data[0].id);
    } catch {
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
     
  }, []);

  const saveCategory = async (values) => {
    try {
      if (catDialog?.mode === "add") {
        const r = await api.post("/content/categories", {
          name: values.name,
          description: values.description || "",
          order: categories.length,
        });
        toast.success("Category added");
        setActiveCat(r.data.id);
      } else if (catDialog?.mode === "edit") {
        await api.put(`/content/categories/${catDialog.data.id}`, {
          name: values.name,
          description: values.description || "",
        });
        toast.success("Category updated");
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
      throw err;
    }
  };

  const deleteCategory = async () => {
    if (!confirmCat) return;
    try {
      await api.delete(`/content/categories/${confirmCat.id}`);
      toast.success("Category deleted");
      if (activeCat === confirmCat.id) setActiveCat(null);
      setConfirmCat(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const saveItem = async (values) => {
    try {
      if (itemDialog?.mode === "add") {
        await api.post("/content/menu-items", {
          category_id: activeCat,
          name: values.name,
          price: values.price || "",
          description: values.description || "",
          order: items.filter((i) => i.category_id === activeCat).length,
        });
        toast.success("Item added");
      } else if (itemDialog?.mode === "edit") {
        await api.put(`/content/menu-items/${itemDialog.data.id}`, {
          name: values.name,
          price: values.price || "",
          description: values.description || "",
        });
        toast.success("Item updated");
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed");
      throw err;
    }
  };

  const deleteItem = async () => {
    if (!confirmItem) return;
    try {
      await api.delete(`/content/menu-items/${confirmItem.id}`);
      toast.success("Deleted");
      setConfirmItem(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const currentItems = items
    .filter((i) => i.category_id === activeCat)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const currentCategory = categories.find((c) => c.id === activeCat);

  return (
    <div>
      <p className="text-sm text-gray-600 mb-6 max-w-2xl">
        Edit menu categories and items. Everything you change here updates the
        public menu instantly.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Categories */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">Categories</div>
            <button
              onClick={() => setCatDialog({ mode: "add" })}
              data-testid="menu-add-category"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          {loading ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : categories.length === 0 ? (
            <div className="text-sm text-gray-500">No categories yet.</div>
          ) : (
            <ul className="space-y-1" data-testid="menu-categories-list">
              {categories.map((c) => (
                <li
                  key={c.id}
                  className={`flex items-center justify-between gap-2 rounded-lg pl-2 pr-1 py-1.5 text-sm cursor-pointer ${
                    activeCat === c.id
                      ? "bg-[var(--terracotta)]/10 text-[var(--terracotta)] font-medium"
                      : "hover:bg-gray-50 text-gray-800"
                  }`}
                  onClick={() => setActiveCat(c.id)}
                  data-testid={`menu-category-item-${c.id}`}
                >
                  <span className="truncate flex-1">{c.name}</span>
                  <span className="flex items-center gap-1 opacity-70">
                    <button
                      className="p-1 hover:text-[var(--terracotta)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCatDialog({ mode: "edit", data: c });
                      }}
                      data-testid={`menu-category-edit-${c.id}`}
                      title="Edit"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      className="p-1 hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmCat(c);
                      }}
                      data-testid={`menu-category-delete-${c.id}`}
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Items */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold">
              {currentCategory?.name || "Select a category"}
            </div>
            {activeCat && (
              <button
                onClick={() => setItemDialog({ mode: "add" })}
                data-testid="menu-add-item"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-[var(--terracotta)] text-white hover:bg-[var(--terracotta-hover)]"
              >
                <Plus size={12} /> New item
              </button>
            )}
          </div>

          {!activeCat ? (
            <EmptyState label="Choose a category on the left." />
          ) : currentItems.length === 0 ? (
            <EmptyState label="No items in this category yet." />
          ) : (
            <table className="w-full text-sm" data-testid="menu-items-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-2">Name</th>
                  <th className="py-2 pr-2">Description</th>
                  <th className="py-2 pr-2 w-24">Price</th>
                  <th className="py-2 pr-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((it) => (
                  <tr
                    key={it.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                    data-testid={`menu-item-row-${it.id}`}
                  >
                    <td className="py-2 pr-2 font-medium">{it.name}</td>
                    <td className="py-2 pr-2 text-gray-600 max-w-md truncate">
                      {it.description || (
                        <span className="italic text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-[var(--terracotta)] font-semibold">
                      {it.price}
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setItemDialog({ mode: "edit", data: it })}
                          className="p-1 text-gray-500 hover:text-[var(--terracotta)]"
                          data-testid={`menu-item-edit-${it.id}`}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => setConfirmItem(it)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          data-testid={`menu-item-delete-${it.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Category dialog */}
      <FormDialog
        open={!!catDialog}
        onOpenChange={(v) => !v && setCatDialog(null)}
        title={catDialog?.mode === "edit" ? "Edit category" : "New category"}
        fields={[
          { name: "name", label: "Category name", placeholder: "e.g. Desserts", required: true },
          { name: "description", label: "Description (optional)", type: "textarea", placeholder: "Short line shown below the category title" },
        ]}
        initialValues={{
          name: catDialog?.data?.name || "",
          description: catDialog?.data?.description || "",
        }}
        submitLabel={catDialog?.mode === "edit" ? "Save changes" : "Add category"}
        onSubmit={saveCategory}
        testIdPrefix="category"
      />

      {/* Item dialog */}
      <FormDialog
        open={!!itemDialog}
        onOpenChange={(v) => !v && setItemDialog(null)}
        title={itemDialog?.mode === "edit" ? "Edit item" : "New menu item"}
        fields={[
          { name: "name", label: "Item name", placeholder: "e.g. Margherita Pizza", required: true },
          { name: "price", label: "Price", placeholder: "$18.99", required: true },
          { name: "description", label: "Description (optional)", type: "textarea", placeholder: "Ingredients & short note" },
        ]}
        initialValues={{
          name: itemDialog?.data?.name || "",
          price: itemDialog?.data?.price || "",
          description: itemDialog?.data?.description || "",
        }}
        submitLabel={itemDialog?.mode === "edit" ? "Save changes" : "Add item"}
        onSubmit={saveItem}
        testIdPrefix="item"
      />

      {/* Delete confirmations */}
      <ConfirmDialog
        open={!!confirmCat}
        onOpenChange={(v) => !v && setConfirmCat(null)}
        title={`Delete category "${confirmCat?.name}"?`}
        description="All items inside this category will also be deleted. This cannot be undone."
        confirmLabel="Delete category"
        onConfirm={deleteCategory}
        testIdPrefix="cat-del-confirm"
      />
      <ConfirmDialog
        open={!!confirmItem}
        onOpenChange={(v) => !v && setConfirmItem(null)}
        title={`Delete "${confirmItem?.name}"?`}
        confirmLabel="Delete item"
        onConfirm={deleteItem}
        testIdPrefix="item-del-confirm"
      />
    </div>
  );
}

/* -------------- Settings Tab -------------- */
const SETTING_FIELDS = [
  { key: "business_name", label: "Business name", type: "text" },
  { key: "tagline", label: "Hero tagline", type: "text" },
  { key: "about", label: "About / Our story", type: "textarea" },
  { key: "address", label: "Address", type: "text" },
  { key: "phone", label: "Phone", type: "text" },
  { key: "hours", label: "Hours", type: "text" },
  { key: "order_url", label: "Order Online URL (Slice)", type: "text" },
  { key: "logo_url", label: "Logo URL (optional)", type: "text" },
  { key: "instagram_url", label: "Instagram URL", type: "text" },
  { key: "facebook_url", label: "Facebook URL", type: "text" },
  { key: "tiktok_url", label: "TikTok URL", type: "text" },
];

function SettingsTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/settings").then((r) => setData(r.data));
  }, []);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const r = await api.put("/content/settings", data);
      setData(r.data);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="text-sm text-gray-600">Loading…</div>;

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-gray-600 mb-6">
        Update the information shown across your website.
      </p>
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        {SETTING_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs uppercase tracking-[0.15em] text-gray-500">
              {f.label}
            </label>
            {f.type === "textarea" ? (
              <textarea
                rows={4}
                value={data[f.key] || ""}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                data-testid={`settings-field-${f.key}`}
                className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[var(--terracotta)]"
              />
            ) : (
              <input
                type="text"
                value={data[f.key] || ""}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                data-testid={`settings-field-${f.key}`}
                className="mt-2 w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[var(--terracotta)]"
              />
            )}
          </div>
        ))}
        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--terracotta)] hover:bg-[var(--terracotta-hover)] text-white font-medium disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save changes
        </button>
      </div>
    </div>
  );
}

/* -------------- Small helpers -------------- */
function IconBtn({ children, onClick, danger, testid, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`p-1.5 rounded-md border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed ${
        danger ? "hover:text-red-600 hover:border-red-200" : ""
      }`}
    >
      {children}
    </button>
  );
}

function EmptyState({ label }) {
  return (
    <div className="border border-dashed border-gray-300 rounded-xl p-10 text-center text-gray-500 text-sm">
      {label}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="aspect-[16/10] bg-gray-100 rounded-xl animate-pulse" />
      ))}
    </div>
  );
}
